<template lang="pug">
v-container
    v-card(light)
        v-card-title Systems
        v-card-text
            v-row
                v-col(cols="12")
                    v-data-table(
                        :headers="headers"
                        :items="localSystems"
                        class="elevation-1"
                        item-key="id"
                        light
                    )

                        template(v-slot:item.system_name="{ item }")
                            span.align-center {{ item.system_name }}
                                v-icon(:color="areAllCategoriesFinalized(item) ? 'green' : 'red'" class="ml-3")
                                    | {{ areAllCategoriesFinalized(item) ? 'check' : 'close' }}


                        template(v-slot:item.actions="{ item }")
                            v-btn(v-if="!deleteEnabled[item.id]" color="grey" @click="enableDelete(item.id)").mr-3 Enable Delete
                            v-tooltip(top v-if="deleteEnabled[item.id]")
                                template(v-slot:activator="{ on, attrs }")
                                    v-btn(
                                        @click="deleteSystem(item.id)"
                                        color="red"
                                        class="mr-3"
                                        v-bind="attrs"
                                        v-on="on"
                                    ) Delete
                                span Delete System (all data will be removed)
                            v-tooltip(top)
                                template(v-slot:activator="{ on, attrs }")
                                    v-btn(
                                        @click="openEditDialog(item)"
                                        class="mr-3"
                                        v-bind="attrs"
                                        v-on="on"
                                    ) Edit
                                span Edit System Parameters
                            v-tooltip(top)
                                template(v-slot:activator="{ on, attrs }")
                                    span(v-bind="attrs" v-on="on")
                                        v-btn(
                                            @click="openCategories(item)"
                                            :disabled="item.categories.length === 0"
                                            class="mr-3"
                                        ) Configure
                                span Configure Categories and Points.
                                    | <br> Requires points accessible through the Infoscan API
                                    | <br> (use Get Points)
                            v-tooltip(top)
                                template(v-slot:activator="{ on, attrs }")
                                    v-btn(
                                        @click="handleDownloadClient(item.id)"
                                        class="mr-3"
                                        bind="attrs"
                                        v-on="on"
                                    ) Download Client
                                span Download Client Installer
                            v-tooltip(top)
                                template(v-slot:activator="{ on, attrs }")
                                    v-btn(
                                        @click="openCreateModelDialog(item)"
                                        class="mr-3"
                                        bind="attrs"
                                        v-on="on"
                                        :disabled="!areAllCategoriesFinalized(item)"
                                    ) Create model
                                span Create a new model for this system (may take an hour or more)
                            v-tooltip(top)
                                template(v-slot:activator="{ on, attrs }")
                                    v-btn(
                                        @click="getPoints(item)"
                                        class="mr-3"
                                        bind="attrs"
                                        v-on="on"
                                    ) Get Points
                                span Obtain Points from remote. Save any changes first!
                            v-progress-circular(
                                v-if="fetchingPoints"
                                :size="24"
                                :width="2"
                                indeterminate
                                color="brand"
                                class="mr-3"
                            )

            v-row(v-if="showCategories")
                v-col(cols="12" )
                    Categories(
                        :categories="selectedSystem?.categories",
                        :systemName="selectedSystem?.system_name",
                        :systemId="selectedSystem?.id",
                        :uom="uom",
                        @close:categories="showCategories = false",
                        @update:categories="handleUpdateCategories"
                    )
        v-card-actions
            v-tooltip(top)
                template(v-slot:activator="{ on, attrs }")
                    v-btn(color="brand" class="white--text" @click="checkForDataLoss" v-bind="attrs" v-on="on") Save All Systems
                span Sends all system changes to Server
            v-btn(
                @click="addSystem"
                :disabled="isDownloading || isAddSystemsDisabled"
                color="orange"
                class="ml-2"
            ) Add System
            v-tooltip(top)
                template(v-slot:activator="{ on, attrs }")
                    v-btn(
                        @click="refresh"
                        class="ml-2"
                        :class="{ 'save-enabled-blink': shouldRefresh}"
                        v-bind="attrs"
                        v-on="on"
                    ) Refresh
                span Reset all changes not already sent to server
            v-tooltip(top)
                template(v-slot:activator="{ on, attrs }")
                    v-btn(
                        @click="goToAnalyticsHome"
                        v-bind="attrs"
                        v-on="on"
                        class="ml-2"
                    ) Analytics Home
                span Go To Advanced Analytics
            v-tooltip(top)
                template(v-slot:activator="{ on, attrs }")
                    v-btn(
                        @click="goToHelp"
                        v-bind="attrs"
                        v-on="on"
                        class="ml-2"
                    ) Help
                span Go To Help
            v-spacer
            span.no-changes-text No changes sent to server without clicking <b>Save All Systems</b>

    v-dialog(v-model="editDialog" max-width="600px")
        v-card(light)
            v-card-title
                v-row(align="center" justify="space-between")
                    span {{ dialogTitle }}
                    v-btn(icon @click="closeEditDialog")
                        v-icon close
            v-card-text
                v-form(ref="editForm", v-model="formValid")
                    v-row()
                        v-col(cols="12")
                            v-text-field(
                                v-model.trim="editedItem.system_name"
                                label="System Name"
                                :rules="[rules.required]"
                                outlined
                                dense
                                persistent-hint
                                color="brand"
                            )
                    v-row()
                        v-col(cols="12")
                            v-text-field(
                                v-model.trim="editedItem.api_key"
                                :label="softwareType"
                                :rules="[rules.required]"
                                outlined
                                dense
                                persistent-hint
                                color="brand"
                            )
                    v-row()
                        v-col(cols="6")
                            v-text-field(
                                v-model.number="editedItem.latitude"
                                label="Latitude"
                                :rules="[rules.isNumber, rules.latitude]"
                                outlined
                                dense
                                type="number"
                                step="0.0001"
                                persistent-hint
                                color="brand"
                            )
                        v-col(cols="6")
                            v-text-field(
                                v-model.number="editedItem.longitude"
                                label="Longitude"
                                :rules="[rules.isNumber, rules.longitude]"
                                outlined
                                dense
                                type="number"
                                step="0.0001"
                                persistent-hint
                                color="brand"
                            )
                    v-row()
                        v-col(cols="12")
                            v-select(
                                v-model="editedItem.timezone"
                                :items="timeZones"
                                label="Time Zone"
                                :rules="[rules.required]"
                                outlined
                                dense
                                persistent-hint
                                color="brand"
                            )
                    v-row()
                        v-col(cols="6")
                            v-text-field(
                                v-model.number="editedItem.flow_production_max"
                                label="Production Max"
                                :rules="[rules.isPositiveInteger, rules.productionMax]"
                                outlined
                                dense
                                type="number"
                                step="1"
                                persistent-hint
                                color="brand"
                            )
                        v-col(cols="6")
                            v-text-field(
                                :value="productionUomDisplay"
                                label="Production UOM"
                                :hint="productionUomHint"
                                :rules="[]"
                                readonly
                                outlined
                                dense
                                persistent-hint
                                color="brand"
                            )
                    v-row()
                        v-col(cols="12" sm="6" md="4")
                            v-text-field(
                                v-model.number="editedItem.flow_storage_max"
                                label="Storage Max"
                                :rules="[rules.isPositiveInteger, rules.storageMax, rules.storageMaxMin]"
                                outlined
                                dense
                                type="number"
                                step="1"
                                persistent-hint
                                color="brand"
                            )
                        v-col(cols="12" sm="6" md="4")
                            v-text-field(
                                v-model.number="editedItem.flow_storage_min"
                                label="Storage Min"
                                :rules="[rules.isPositiveInteger, rules.storageMin, rules.storageMinMax]"
                                outlined
                                dense
                                type="number"
                                step="1"
                                persistent-hint
                                color="brand"
                            )
                        v-col(cols="12" sm="6" md="4")
                            v-text-field(
                                :value="storageUomDisplay"
                                label="Storage UOM"
                                :hint="storageUomHint"
                                :rules="[]"
                                readonly
                                outlined
                                dense
                                persistent-hint
                                color="brand"
                            )
                    v-row()
                        v-col(cols="6")
                            v-text-field(
                                v-model="editedItem.flow_plant_start_time"
                                label="Plant Start Time (HH:mm)"
                                :rules="[rules.required, rules.plantStartTime]"
                                outlined
                                dense
                                persistent-hint
                                color="brand"
                                class="no-clipping"
                            )
                        v-col(cols="6")
                            v-text-field(
                                v-model="editedItem.id"
                                label="system id (read-only)"
                                readonly
                                outlined
                                dense
                                color="brand"
                            )
            v-card-actions
                v-spacer
                v-btn(color="green" :disabled="!formValid" @click="saveEdit") Save
                v-btn(color="grey" @click="closeEditDialog") Cancel

    v-dialog(v-model="showWarningDialog", persistent, max-width="500")
        v-card(light)
            v-card
                v-card-title.headline Data Loss Warning
                v-card-text
                    p Making these changes will result in data loss and require a new model to be created.  Are you sure you want to continue?
                    ul.change-list
                        li(
                            v-for="(change, field) in pendingChanges"
                            :key="field"
                        )
                            div {{ formatFieldName(field) }}
                                .change-details
                                    span From: {{ change.from || 'empty' }}
                                    span.mx-2 →
                                    span To: {{ change.to || 'empty' }}

                v-card-actions
                    v-btn(
                        color="red darken-1"
                        @click="proceedWithChanges"
                    ) I Know What I'm Doing
                    v-spacer
                    v-btn(
                        color="grey"
                        @click="cancelChanges"
                    ) Cancel


    v-dialog(v-model='isDownloading', persistent, max-width='500')
        v-card(light)
            v-card-title
                p {{clientInstallerState}}
            v-card-text
                v-row
                    v-col(cols='12')
                        v-progress-linear(:value='percentDone', color='brand', striped, height='24')
                            template(v-slot:default='{value}')
                                strong.white--text {{ Math.ceil(value) }}%


    v-dialog(v-model="showDataLossDialog", persistent, max-width="500")
        v-card(light)
            v-card-title Data Loss Warning
            v-card-text
                p  Making these changes will result in data loss and require a new model to be created.  Are you sure you want to continue?
                v-simple-table
                    template(v-slot:default)
                        thead
                            tr
                                th System Name
                                th Flow Data
                                th Weather Data
                        tbody
                            tr(v-for="system in systemsDataLoss")
                                td {{ system.name }}
                                td {{ system['flow'] ? 'X' : '' }}
                                td {{ system['weather'] ? 'X' : '' }}
            v-card-actions
                v-btn(color="red", @click="saveAllSystems", class="black--text") I Know What I'm Doing
                v-spacer
                v-btn(color="grey", @click="showDataLossDialog = false") Cancel

    v-dialog(v-model="showCreateModelDialog", max-width="500px")
        v-card(light)
            v-card-title.headline Create Model Confirmation
            v-card-text
                p You are about to create a new model for the system:
                    span(class="font-weight-bold") {{ ' ' + selectedSystem?.system_name }}.
                p This process may take an hour or more. Do you want to proceed?
                template(v-if="hasActiveModel")
                    h3 Current model:
                    ul
                        li Created: {{ activeModelDate }}
                        li Version: {{ activeModelVersion }}

                template(v-else)
                    p No active model found for this system.

                template(v-if="lastModelFailed")
                    h3 Last model attempt Failed
                    ul
                        li When: {{ lastModelFailedDate }}
                        li(style="color: red") Reason: {{ lastModelFailReason }}

                template(v-if="modelReason")
                    h3 Eligibility:
                    p
                        span(v-if="canCreateNewModel")  ✅ Ready to build
                        span(v-else) ❌ {{  modelBuildPending ?  "Model creation already queued" : eligibilityReason }}

            v-card-actions
                v-btn(color="grey" @click="cancelCreateModel") Cancel
                v-spacer
                v-btn(color="green" @click="confirmCreateModel" :disabled="!canCreateNewModel") OK

    v-dialog(v-model="showDeleteDialog", max-width="500px")
        v-card(light)
            v-card-title.headline Delete System Confirmation
            v-card-text
                p Are you sure you want to delete this system? This action cannot be undone.
            v-card-actions
                v-btn(color="grey" @click="showDeleteDialog = false") Cancel
                v-spacer
                v-btn(color="red" @click="confirmDelete") Delete

    v-snackbar(
        v-model="snackbar"
        :timeout="timeout"
        :color="color"
        :multi-line="multiLine"
    )
        span(:style="{ color: textColor }") {{ message }}

        template(v-slot:action="{ attrs }")
            v-btn(text v-bind="attrs" @click="snackbar = false") Close
</template>

<script>

import {nanoid} from 'nanoid';
import mergeDeep from 'lodash/merge';
import Categories from './Categories.vue';
import { mapActions, mapGetters } from 'vuex';

const LIMITS = {
    STORAGE_MAX: 4294967295, // max for unsigned int in Maria/MySQL
    STORAGE_MIN: 0,
    PRODUCTION_MAX: 4294967295,
    PRODUCTION_MIN: 0
};

export default {
    name: 'SystemsSettings',
    emits: ['update:systems', 'refresh', 'save:systems', 'download:client', 'create:model'],
    components: {
        Categories
    },
    props: {
        customerId: {
            type: Number,
            required: true
        },
        systems: {
            type: Array,
            default: () => []
        },
        uom: {
            type: Array,
            required: true
        },
    },
    data () {
        return {
            deleteEnabled: {},
            isDownloading: false,
            selectedSystem: null,
            showCategories: false,
            percentDone: 0,
            clientInstallerState: '',
            showDataLossDialog: false,
            showDeleteDialog: false,
            systemToDelete: null,
            headers: [
                { text: 'Name', value: 'system_name' },
                { text: 'Actions', value: 'actions', sortable: false },
            ],
            rules: {
                required: (value) => !!value || 'Required.',
                isNumber: (value) => !Number.isNaN(Number.parseFloat(value)) || 'Must be a number.',
                isPositiveInteger: (value) => Number.isInteger(value) && value >= 0 || 'Must be a positive integer.',
                latitude: (value) => value >= -90 && value <= 90 || 'Must be between -90 and 90.',
                longitude: (value) => value >= -180 && value <= 180 || 'Must be between -180 and 180.',
                productionMax: (value) => value >= LIMITS.PRODUCTION_MIN && value <= LIMITS.PRODUCTION_MAX || `Must be between ${LIMITS.PRODUCTION_MIN} and ${LIMITS.PRODUCTION_MAX}`,
                storageMax: (value) => value >= LIMITS.STORAGE_MIN && value <= LIMITS.STORAGE_MAX || `Must be between ${LIMITS.STORAGE_MIN} and ${LIMITS.STORAGE_MAX}.`,
                storageMin: (value) => value >= LIMITS.STORAGE_MIN && value <= LIMITS.STORAGE_MAX || `Must be between ${LIMITS.STORAGE_MIN} and ${LIMITS.STORAGE_MAX}.`,
                plantStartTime: (value) => /^([01]?\d|2[0-3]):[0-5]\d$/.test(value) || 'Must be a valid time in HH:mm format.',
                storageMaxMin: (value) => value >= this.editedItem.flow_storage_min || 'Storage Max must be greater than Storage Min.',
                storageMinMax: (value) => value <= this.editedItem.flow_storage_max || 'Storage Min must be less than Storage Max.',
            },
            timeZones: [
                // Eastern Time
                'America/New_York',

                // Central Time
                'America/Chicago',

                // Mountain Time (with and without DST)
                'America/Denver',    // DST-observing Mountain Time
                'America/Phoenix',   // Arizona - Mountain Standard, no DST

                // Pacific Time
                'America/Los_Angeles',

                // Alaska Time
                'America/Anchorage',

                // Hawaii-Aleutian Time
                'America/Adak'
            ],
            localSystems: mergeDeep([], this.systems),
            defaultItem: {
                id: null,
                system_name: '',
                latitude: null,
                longitude: null,
                flow_production_max: null,
                flow_production_uom: '',
                flow_storage_max: null,
                flow_storage_min: null,
                flow_storage_uom: '',
                flow_plant_start_time: '12:00',
                customerId: this.customerId,
                api_key: '',
                categories: [],
                timezone: '',
                isDrastic: false
            },
            editDialog: false,
            editedItem: { ...this.defaultItem },
            newItem: null,
            formValid: false,

            // dangerous changes dialog
            showWarningDialog: false,
            pendingChanges: null,
            progressColor: 'orange',

            // snackbar
            alertMessage: '',
            snackbar: false,
            message: "",
            color: "brand",
            textColor: "#ffffff",
            timeout: 3000,
            multiLine: false,

            rotateValue: 0,

            showCreateModelDialog: false,
            selectedSystemForModel: null,
            modelInfo: {},
            modelEligible: {},

            fetchingPoints: false,
            shouldRefresh: false,
        };
    },
    computed: {
        ...mapGetters([
            'customerData',
            'systemsDataLoss',
            'error',
            'errorMessage',
            'analyticsKey'
        ]),

        dialogTitle () {
            return this.newItem ? 'Add System' : 'Edit System';
        },
        isAddSystemsDisabled () {
            return this.localSystems.length > 0;
        },
        softwareType () {
            return `API Key for talking to system software`;
        },

        eligibilityReason () {
            if (!this.selectedSystemForModel) return null;
            return this.modelEligible?.reason;
        },

        canCreateNewModel () {
            if (!this.selectedSystemForModel) return false;
            return this.modelEligible?.canCreate === true && !this.modelInfo?.pendingModelCreation;
        },

        modelBuildPending () {
            return this.modelInfo?.pendingModelCreation === true;
        },

        hasActiveModel () {
            return !!this.modelInfo?.activeModel;
        },

        lastModelFailed () {
            return this.modelInfo?.lastAttemptFailed || false;
        },

        lastModelFailReason () {
            // limit to the first line of the error message
            const {lastAttemptFailMessage} = this.modelInfo || {};

            if (!lastAttemptFailMessage) return '';
            return lastAttemptFailMessage.split('\n')[0] || '';
        },

        lastModelFailedDate () {
            if(!this.modelInfo?.lastAttemptFailed) return '';
            const updated = this.modelInfo.lastAttemptFailDate;
            return updated;
        },

        activeModelDate () {
            if (!this.hasActiveModel) return '';
            const created = this.modelInfo.activeModel.createdAt;
            return created;
        },

        activeModelVersion (){
            if (!this.hasActiveModel) return '';
            return this.modelInfo.activeModel?.model_version;
        },

        modelReason () {
            return this.modelEligible?.reason || '';
        },

        categoryByName () {
            const categories = this.editedItem?.categories || [];
            return categories.reduce((acc, category) => {
                acc[category.category_name] = category;
                return acc;
            }, {});
        },

        storageCategoryUomCode () {
            const storageCategory = this.categoryByName.STORAGE;
            return this.getUomCodeFromCategory(storageCategory);
        },

        productionCategoryUomCode () {
            const productionCategory = this.categoryByName.PRODUCTION;
            return this.getUomCodeFromCategory(productionCategory);
        },

        storageUomDisplay () {
            return this.storageCategoryUomCode || 'Set at category level';
        },

        productionUomDisplay () {
            return this.productionCategoryUomCode || 'Set at category level';
        },

        storageUomHint () {
            return this.storageCategoryUomCode
                ? 'Derived from STORAGE category UOM'
                : 'Category UOM must be set at category level (STORAGE).';
        },

        productionUomHint () {
            return this.productionCategoryUomCode
                ? 'Derived from PRODUCTION category UOM'
                : 'Category UOM must be set at category level (PRODUCTION).';
        }

    },
    methods: {
        ...mapActions ([
            'generateClientFile',
            'checkForSystemsDataLoss',
            'checkModelEligibility',
            'fetchModelInfo',
            'fetchPoints'
        ]),

        getUomCodeFromCategory (category) {
            const commonUomId = category?.common_uom_id;
            if (!commonUomId || commonUomId <= 1) {
                return '';
            }

            const unit = (this.uom || []).find(uom => uom.id === commonUomId);
            return unit?.uom_code || '';
        },

        syncFlowUomFromCategories () {
            this.editedItem.flow_storage_uom = this.storageCategoryUomCode || '';
            this.editedItem.flow_production_uom = this.productionCategoryUomCode || '';
        },

        async prepareModelDialog (systemId) {
            try {
                const [i,e] = await Promise.all([
                    this.fetchModelInfo(systemId),
                    this.checkModelEligibility(systemId)
                ]);
                this.modelInfo = i || {};
                this.modelEligible = e || {};
            } catch(err){
                console.error('Error fetching model info:', err);
                this.showSnackbar({
                    message: 'Failed to load model info. Please try again.',
                    type: 'error',
                });
            }

        },

        async openCreateModelDialog (system) {
            this.selectedSystemForModel = system;
            this.selectedSystem = system;

            try {
                await this.prepareModelDialog(system.id);
                await this.$nextTick(() => {
                    setTimeout(() =>{
                        this.showCreateModelDialog = true;
                    }, 500);
                });

            } catch (err) {
                console.error('Failed to load model info:', err);
                this.showSnackbar({
                    message: 'Failed to load model info. Please try again.',
                    type: 'error',
                });
            }

        },

        cancelCreateModel () {
            this.showCreateModelDialog = false;
            this.selectedSystemForModel = null;
        },

        confirmCreateModel () {
            if (this.selectedSystemForModel) {
                this.$emit('create:model', this.selectedSystemForModel.id);
                this.showSnackbar({
                    message: 'Model creation started. This may take a while. You will be notified by e-mail when it is complete.',
                    type: 'warning',
                });
            }
            this.showCreateModelDialog = false;
            this.selectedSystemForModel = null;
        },

        splitPath (filePath) {
            // Detect the separator used in the path string
            const separator = filePath.includes('\\') ? '\\' : '/';
            return filePath.split(separator);
        },

        /**
         *
         * @param {object} options
         * @param {string} options.message - The message to display in the snackbar
         * @param {string} options.color - default is red
         * @param {number} options.timeout - default is 3000 milliseconds
         */
        showSnackbar ({message, color = "brand", textColor = "#ffffff", timeout = 3000, type = null}) {
            if (type === 'warning') {
                color = 'orange';
                textColor = '#000000';
            } else if (type === 'error') {
                color = '#8B0000';
                textColor = '#ffffff';
            }
            this.message = message;
            this.color = color;
            this.textColor = textColor;
            this.timeout = timeout;
            this.snackbar = true;
        },

        async handleDownloadClient (systemId) {
            await this.$nextTick();

            this.isDownloading = true;

            this.percentDone = 0;

            const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

            // Start a timer to increment percentDone
            const interval = setInterval(() => {
                if (this.percentDone < 99) {
                    this.percentDone += 1;
                    this.updateProgressLinear();
                }
            }, 500);

            try {
                this.downloadInProgress = true;
                this.clientInstallerState = 'Generating client ...';
                const {error, file} = await this.generateClientFile(systemId);
                if(error) {
                    throw new Error('Failed to generate client file');
                }
                const myFile = encodeURIComponent(file.path);

                const url = `downloadFileAny?file=${myFile}`;
                // the file size is about 22 MB
                this.clientInstallerState = 'Preparing download ...';
                const fileToDownload = await this.axiosDownload(url, 'application/x-msdownload');

                if (fileToDownload) {
                    const objectUrl = window.URL.createObjectURL(fileToDownload);
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.setAttribute('download', file.name);
                    document.body.appendChild(link);
                    await delay(200);
                    link.click();
                    console.log('link clicked');
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(objectUrl); // Free up memory
                    this.alertMessage = `The client file is generated and will be downloaded shortly.`;
                } else {
                    throw new Error('fileToDownload is undefined');
                }
            } catch {
                this.showSnackbar({message:'Error downloading client file. Try again or Contact engineering@dorsettcontrols.com for assistance', type: 'warning'});
            } finally {
                this.percentDone = 100;
                clearInterval(interval);
                this.isDownloading = false;
                this.downloadInProgress = false;
                this.clientInstallerState = '';
            }
        },

        /**
         *
         * @param {object[]} categories - Array of categories to update
         */
        handleUpdateCategories (categories) {
            if (!Array.isArray(categories)) {
                console.error('Invalid categories:', categories);
                return;
            }

            // find the system in the config
            const index = this.localSystems.findIndex(system => system.id === this.selectedSystem.id);
            if (index === -1) {
                console.warn('Could not find system in config');
                return;
            }

            let updatedSystem = mergeDeep({}, this.localSystems[index]);
            updatedSystem.categories = categories.map(cat => ({
                ...cat,
                points: [...cat.points]
            }));

            // Use $set to properly update the array element
            this.$set(this.localSystems, index, updatedSystem);

            const clonedLocalSystems = mergeDeep([], this.localSystems);

            // update the store
            this.$emit('update:systems', clonedLocalSystems);
        },

        openCategories (system) {
            if(this.showCategories) {
                this.showCategories = false;
                this.selectedSystem = null;
            } else {
                this.selectedSystem = system;
                this.showCategories = true;
            }
        },

        refresh () {
            this.$emit('refresh');
            this.deleteEnabled = {};
            this.shouldRefresh = false;
        },

        // adds to local data only
        addSystem () {
            this.newItem = {
                ...this.defaultItem,
                id: nanoid(10),
                customerId: this.customerId,
            };
            this.openEditDialog(this.newItem);
        },

        // saves all systems
        saveAllSystems () {
            this.showDataLossDialog = false;
            this.$emit('save:systems', this.localSystems);
        },

        // determines if any of the pending changes will result in data loss
        async checkForDataLoss () {
            await this.checkForSystemsDataLoss(this.localSystems);
            if(this.error) {
                this.showDataLossDialog = false;
                this.showSnackbar({message:'Saving systems failed. Please try again.', type: 'error'});
                return;
            }

            if(this.systemsDataLoss.length > 0) {
                this.showDataLossDialog = true;
            } else {
                this.saveAllSystems();
            }
        },

        // removes a local system
        deleteSystem (id) {
            this.systemToDelete = id;
            this.showDeleteDialog = true;
        },

        confirmDelete () {
            this.$set(this, 'localSystems', this.localSystems.filter(system => system.id !== this.systemToDelete));
            this.emitSystemsUpdate();
            this.showDeleteDialog = false;
            this.systemToDelete = null;
        },

        openEditDialog (item) {
            this.editedItem = { ...item };
            if (item.id) {
                // if editing an existing item, set the start time to the correct format hh:mm
                this.editedItem.flow_plant_start_time = this.editedItem.flow_plant_start_time.split(':').slice(0, 2).join(':');
            }
            this.syncFlowUomFromCategories();
            this.editDialog = true;
            this.$nextTick(() => {
                this.$refs.editForm.validate();
            });
        },

        closeEditDialog () {
            this.editDialog = false;
            this.editedItem = { ...this.defaultItem };
            this.newItem = null;
        },

        saveEdit () {
            if (this.$refs.editForm.validate()) {
                this.syncFlowUomFromCategories();
                if (this.newItem) {
                    this.$set(this.localSystems, this.localSystems.length, {...this.editedItem});
                    this.closeEditDialog();
                } else {
                    const originalSystem = this.localSystems.find(system => system.id === this.editedItem.id);
                    const changedFields = {};
                    const finalizedSystem = this.areAllCategoriesFinalized(originalSystem);

                    // timezone: wipe all weather, flow data, forecast data. forecast_info, Create new model
                    // flow_production_uom: wipe all flow data, forecast data, forecast_info. Create new model
                    // flow_storage_uom: wipe all flow data, forecast data, forecast_info. Create new model
                    // latitude: wipe all weather, forecast data. forecast_info, Create new model
                    // longitude: wipe all weather, forecast data. forecast_info, Create new model

                    // these only are impactful if the system is finalized
                    if(finalizedSystem) {
                        ['latitude', 'longitude', 'timezone'].forEach(field => {
                            if (originalSystem[field] !== this.editedItem[field]) {
                                changedFields[field] = {
                                    from: originalSystem[field],
                                    to: this.editedItem[field]
                                };
                            }

                        });

                        if (Object.keys(changedFields).length > 0) {
                            this.pendingChanges = changedFields;
                            this.warningMessage = `You are about to change critical fields: ${Object.keys(changedFields).join(', ')}.\nThese changes may affect system calculations.\nDo you want to proceed?`;
                            this.showWarningDialog = true;
                            return;
                        }
                    }
                    this.commitChanges();
                }
            }
        },

        proceedWithChanges () {
            this.showWarningDialog = false;
            this.commitChanges(true);
        },

        cancelChanges () {
            this.showWarningDialog = false;
            this.pendingChanges = null;
        },

        commitChanges (isDrastic = false) {
            const index = this.localSystems.findIndex(system => system.id === this.editedItem.id);
            if (index !== -1) {
                this.$set(this.localSystems, index, this.editedItem);
                this.emitSystemsUpdate();
            }
            this.closeEditDialog();
        },

        formatFieldName (field) {
            const fieldMap = {
                'latitude': 'Latitude',
                'longitude': 'Longitude',
                'flow_production_uom': 'Production Unit of Measure',
                'flow_storage_uom': 'Storage Unit of Measure',
                'timezone': 'Timezone'
            };
            return fieldMap[field] || field;
        },

        emitSystemsUpdate () {
            this.$emit('update:systems', {
                systems: mergeDeep([], this.localSystems)
            });
        },

        updateProgressLinear () {
            if (this.percentDone < 50) {
                this.progressColor = 'red';
            } else if (this.percentDone < 80) {
                this.progressColor = 'orange';
            } else {
                this.progressColor = 'green';
            }
        },

        areAllCategoriesFinalized (system) {
            if(system.categories.length === 0) {
                return false;
            }
            return system.categories.every(category => category.is_finalized);
        },

        enableDelete (id) {
            this.$set(this.deleteEnabled, id, true);
        },

        goToAnalyticsHome () {
            this.$router.push('/customer/analytics');
        },

        goToHelp () {
            this.$router.push('/customer/resources');
        },

        /**
         *
         * @param {object} system - analytics system
         */
        async getPoints (system) {
            // validate that the system has been saved
            const localSystem = this.localSystems.find(s => s.id === system.id);
            const storeSystem = this.systems.find(s => s.id === system.id);

            if (!storeSystem) {
                this.showSnackbar({message: 'Please save the system before testing connectivity.', type: 'warning'});
                return;
            }

            if (!localSystem) {
                this.showSnackbar({message: 'Local system not found. Please refresh and try again.', type: 'warning'});
                return;
            }
            if (localSystem.api_key !== storeSystem.api_key) {
                this.showSnackbar({message: 'API key has changed. Please save the system before testing connectivity.', type: 'warning'});
                return;
            }

            try {
                this.fetchingPoints = true;
                const result = await this.fetchPoints({systemId: system.id});
                if(!result.success) {
                    this.showSnackbar({message: 'Failed to fetch points. Please try again.', type: 'error'});
                } else {
                    this.shouldRefresh = true;
                    this.showSnackbar({message: 'Points fetched successfully. Click Refresh.', type: 'success'});
                }
            } catch(err){
                this.showSnackbar({message: `Failed to fetch points. ${err.message}`, type: 'error'});
            } finally {
                // nothing to do
                this.fetchingPoints = false;
            }

        }

    },
    watch: {
        systems: {
            handler (newSystems){
                this.localSystems = mergeDeep([], newSystems);
                this.selectedSystem = null;
                this.showCategories = false;
            },
            deep: true,
            immediate: true
        }
    },

    mounted () {
        this.localSystems = mergeDeep([], this.systems);
        this.$root.$on('show:alert', ({message, type}) => {
            const data = {message};
            if(type) {
                data.type = type;
            }

            this.showSnackbar(data);
        });
        this.deleteEnabled = {};
    },

    beforeDestroy () {
        this.$root.$off('show:alert', this.showAlert);
    },
};
</script>

<style scoped>
.align-center {
    display: flex;
    align-items: center;
}
.no-changes-text {
    margin-left: 16px;
    color: gray;
}
.no-clipping .v-label {
    white-space: nowrap;
}
.change-list {
    list-style-type: disc;
    padding-left: 20px;
    margin: 8px 0;
}
.change-details {
    color: #666;
    font-size: 0.9em;
    margin-left: 8px;
}

.v-progress-circular {
  margin: 1rem;
}

.save-enabled-blink {
  animation: blink-enabled 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  box-shadow: 0 0 12px 2px #43a047;
  transform: scale(1.04);
}
@keyframes blink-enabled {
  0% {
    box-shadow: 0 0 0px #43a047, 0 0 0px #fff;
    transform: scale(1.0);
  }
  20% {
    box-shadow: 0 0 12px 2px #43a047, 0 0 4px #fff;
    transform: scale(1.04);
  }
  50% {
    box-shadow: 0 0 24px 6px #43a047, 0 0 8px #fff;
    transform: scale(1.08);
  }
  80% {
    box-shadow: 0 0 12px 2px #43a047, 0 0 4px #fff;
    transform: scale(1.04);
  }
  100% {
    box-shadow: 0 0 0px #43a047, 0 0 0px #fff;
    transform: scale(1.0);
  }
}

</style>
